import type { AnyRatingServiceIncludingValue } from '@/stores/ratingService';

export type ArchivalMode =
  | 'never'
  | 'always-archive'
  | 'always-unarchive'
  | 'vote-exists'
  | 'vote-exists-both'
  | 'positive-vote'
  | 'positive-vote-or-unarchive'
  | 'custom';

// ── Custom rule data model (used by task 25's rule editor UI) ──────────────

export type RuleLogicOperator = 'AND' | 'OR';
export type ComparisonOperator = '=' | '≠' | '<' | '≤' | '≥' | '>';
export type LikeMatch = 'up' | 'down' | 'any' | 'none';

interface ServiceConditionBase {
  type: 'service';
  serviceKey: string;
  serviceName: string;
  negated: boolean;
}

export interface LikeServiceCondition extends ServiceConditionBase {
  serviceType: 'like';
  match: LikeMatch;
}

export interface NumericServiceCondition extends ServiceConditionBase {
  serviceType: 'stars' | 'counter';
  operator: ComparisonOperator;
  value: number;
}

export type ServiceCondition = LikeServiceCondition | NumericServiceCondition;

export interface RuleGroup {
  type: 'group';
  operator: RuleLogicOperator;
  negated: boolean;
  items: RuleConditionItem[];
}

export type RuleConditionItem = ServiceCondition | RuleGroup;

/** Archive if archiveCondition matches; unarchive if unarchiveCondition matches; archive takes priority. */
export interface CustomArchivalRules {
  archiveCondition: RuleGroup | null;
  unarchiveCondition: RuleGroup | null;
}

export type CustomArchivalResult = 'archive' | 'unarchive' | 'unchanged';

// ── Evaluator ──────────────────────────────────────────────────────────────

function matchLike(match: LikeMatch, value: boolean | null): boolean {
  switch (match) {
    case 'any': return value !== null;
    case 'none': return value === null;
    case 'up': return value === true;
    case 'down': return value === false;
  }
}

function compareNumeric(op: ComparisonOperator, actual: number, threshold: number): boolean {
  switch (op) {
    case '=': return actual === threshold;
    case '≠': return actual !== threshold;
    case '<': return actual < threshold;
    case '≤': return actual <= threshold;
    case '≥': return actual >= threshold;
    case '>': return actual > threshold;
  }
}

function evaluateServiceCondition(
  condition: ServiceCondition,
  ratings: AnyRatingServiceIncludingValue[],
): boolean {
  const rating = ratings.find((r) => r.service_key === condition.serviceKey);
  if (!rating) return condition.negated;

  let result: boolean;
  if (condition.serviceType === 'like') {
    result = matchLike(condition.match, rating.value as boolean | null);
  } else {
    const numVal = rating.value as number | null;
    result = numVal !== null && compareNumeric(condition.operator, numVal, condition.value);
  }
  return condition.negated ? !result : result;
}

function evaluateRuleGroup(
  group: RuleGroup,
  ratings: AnyRatingServiceIncludingValue[],
): boolean {
  if (group.items.length === 0) {
    const emptyResult = group.operator === 'AND';
    return group.negated ? !emptyResult : emptyResult;
  }
  const results = group.items.map((item) =>
    item.type === 'group'
      ? evaluateRuleGroup(item, ratings)
      : evaluateServiceCondition(item, ratings),
  );
  const combined = group.operator === 'AND' ? results.every(Boolean) : results.some(Boolean);
  return group.negated ? !combined : combined;
}

export function evaluateCustomRules(
  rules: CustomArchivalRules,
  ratings: AnyRatingServiceIncludingValue[],
): CustomArchivalResult {
  if (rules.archiveCondition && evaluateRuleGroup(rules.archiveCondition, ratings)) {
    return 'archive';
  }
  if (rules.unarchiveCondition && evaluateRuleGroup(rules.unarchiveCondition, ratings)) {
    return 'unarchive';
  }
  return 'unchanged';
}

// ── Decision entry point ───────────────────────────────────────────────────

/**
 * Check if a rating has a positive vote (upvote).
 * - LikeService: true = upvote
 * - StarsService: > 0 = upvote
 * - CounterService: > 0 = upvote
 */
function hasPositiveVote(rating: AnyRatingServiceIncludingValue): boolean {
  if (rating.value === null) return false;
  if (typeof rating.value === 'boolean') {
    // LikeService: true = upvote
    return rating.value === true;
  }
  // StarsService or CounterService: > 0 = upvote
  return rating.value > 0;
}

/**
 * Compute whether to archive (true), unarchive (false), or do nothing (null)
 * based on the archival mode and current vote values.
 */
export function computeArchivalDecision(
  mode: ArchivalMode,
  ratings: AnyRatingServiceIncludingValue[],
  customRules?: CustomArchivalRules | null,
): boolean | null {
  switch (mode) {
    case 'never':
      return null;
    case 'always-archive':
      return true;
    case 'always-unarchive':
      return false;
    case 'vote-exists': {
      const anySet = ratings.some((r) => r.value !== null);
      return anySet ? true : null;
    }
    case 'vote-exists-both': {
      const anySet = ratings.some((r) => r.value !== null);
      return !!anySet;
    }
    case 'positive-vote': {
      const hasPositive = ratings.some(hasPositiveVote);
      return hasPositive ? true : null;
    }
    case 'positive-vote-or-unarchive': {
      const hasPositive = ratings.some(hasPositiveVote);
      return !!hasPositive;
    }
    case 'custom': {
      if (!customRules) return null;
      const result = evaluateCustomRules(customRules, ratings);
      if (result === 'archive') return true;
      if (result === 'unarchive') return false;
      return null;
    }
  }
}
